"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Zap, Wand } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LeBateleurCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Le Bateleur</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot de Marseille
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Le Bateleur</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-le-bateleur.svg"
                  alt="Le Bateleur Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-red-900/40 to-orange-900/40 border border-red-500/30 flex-col items-center justify-center text-red-300">
                  <span className="text-3xl font-bold mb-2">I</span>
                  <span className="text-lg font-semibold">LE BATELEUR</span>
                  <span className="text-sm text-center mt-2">The Magician</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">I</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Le Bateleur</h1>
                  <p className="text-xl text-red-400">Soytarı / Manifestasyon Gücü</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğindeki ilk numaralı kart. Masasının başında duran figür, dört elementi
                kontrol etme gücünü ve yaratıcı potansiyeli temsil eder. RWS'den farklı olarak daha sade ama güçlü sembolizm kullanır.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Yaratıcılık</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Beceri</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Başlangıç</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Manifestasyon</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yaratıcı Güç</h3>
                  <p className="text-slate-400 text-sm">Kendi kaderini yaratma gücü, öz güven ve beceri.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yeni Başlangıçlar</h3>
                  <p className="text-slate-400 text-sm">Projelere başlama, girişkenlik ve inisiyatif alma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">El Becerisi</h3>
                  <p className="text-slate-400 text-sm">Manuel yetenekler, zanaatkarlık ve pratik çözümler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İletişim</h3>
                  <p className="text-slate-400 text-sm">Etkili konuşma, ikna etme ve ticaret yetenekleri.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aldatma</h3>
                  <p className="text-slate-400 text-sm">Hilekarlık, sahtecilik ve güvenilmezlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Beceriksizlik</h3>
                  <p className="text-slate-400 text-sm">Yetersiz hazırlık, acemilik ve başarısızlık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kaynak İsrafı</h3>
                  <p className="text-slate-400 text-sm">Yetenekleri boşa harcama, dağınıklık ve verimsizlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Özgüven Eksikliği</h3>
                  <p className="text-slate-400 text-sm">Kendine güvenmeme, kararsızlık ve erteleme.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marseille Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-brown-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">📱</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Masa</h3>
              <p className="text-slate-400 text-sm">Çalışma alanı, beceri ve hazırlık. Manifestasyon için gerekli araçlar.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Wand className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Değnek</h3>
              <p className="text-slate-400 text-sm">İrade gücü, yönlendirme ve otoriteyi temsil eder.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🎩</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Şapka</h3>
              <p className="text-slate-400 text-sm">Sonsuzluk şeklindeki şapka, sınırsız potansiyeli gösterir.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⚗️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Araçlar</h3>
              <p className="text-slate-400 text-sm">Masadaki objeler dört elementi ve yaratıcı araçları simgeler.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌿</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Doğal Duruş</h3>
              <p className="text-slate-400 text-sm">Rahatlık ve güven içinde duran figür, ustalığı gösterir.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">✋</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">El Pozisyonu</h3>
              <p className="text-slate-400 text-sm">Bir el yukarıda, bir el aşağıda - enerji akışını yönlendirir.</p>
            </div>
          </div>
        </div>

        {/* Marseille vs RWS */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille vs Rider-Waite</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Marseille Yaklaşımı</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Daha sade, geometrik sembolizm</li>
                <li>• Sezgisel yorumlama gerektir</li>
                <li>• Geleneksel Avrupa geleneği</li>
                <li>• "Bateleur" - soytarı/akrobat anlamı</li>
                <li>• Daha az detay, daha çok özgürlük</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Rider-Waite Farkları</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Daha detaylı görsel anlatım</li>
                <li>• Sonsuzluk sembolü daha belirgin</li>
                <li>• Golden Dawn etkileri güçlü</li>
                <li>• "Magician" - büyücü vurgusu</li>
                <li>• Yeni başlayanlar için kolay</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yeni ilişki başlangıcı</li>
                <li>• Flört ve çekicilik</li>
                <li>• İletişim yetenekleri</li>
                <li>• Romantik girişkenlik</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Girişimcilik fırsatları</li>
                <li>• Yeni proje başlangıcı</li>
                <li>• Satış ve pazarlama</li>
                <li>• El becerisi gerektiren işler</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yetenekleri keşfetme</li>
                <li>• Öz güven geliştirme</li>
                <li>• Yaratıcılığı artırma</li>
                <li>• Pratik beceriler</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Manifestasyon teknikleri</li>
                <li>• Yaratıcı enerji kullanımı</li>
                <li>• Ruhsal uyanış başlangıcı</li>
                <li>• İrade gücü geliştirme</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel Marseille tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Majör Arkana
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-papesse"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            La Papesse
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}